/**
 * BOS Overlay Primitive
 *
 * Renders Break-of-Structure / CHoCH short horizontal level segments AND their
 * text labels entirely on canvas, instead of as lightweight-charts LineSeries.
 *
 * WHY: a LineSeries adds time-scale data points (logical indices) for every BOS
 * level. Those extra points perturb the shared time scale and break the chart's
 * load-time snap-to-live-edge (candles no longer load at the current position).
 * A canvas primitive paints at coordinates derived from the EXISTING time scale
 * without contributing any data points, so the time scale is untouched.
 *
 * Each overlay item carries BOTH a line segment and an (optional) label. The
 * line passes THROUGH the vertical center of the text, mirroring the previous
 * LineSeries + label-on-line behaviour.
 *
 * Mirrors PriceLevelLabelsPrimitive: an ISeriesPrimitive attached to the
 * candlestick series. Coordinates are computed each frame from
 * timeToCoordinate(breakTime/swingTime) and priceToCoordinate(price).
 */

import {
  ISeriesPrimitive,
  SeriesAttachedParameter,
  Time,
  IPrimitivePaneRenderer,
  IPrimitivePaneView,
  PrimitivePaneViewZOrder,
} from 'lightweight-charts';
import {
  LAYOUT_PRIORITY,
  MarkerLayoutClient,
  type LayoutContext,
  type LayoutItem,
  type MarkerLayoutRegistry,
} from './markerLayout';

// ============================================================================
// Pulse tuning (preliminary "hot/forming" glow)
// ============================================================================

// A newly-fired preliminary break flashes a brighter glow that eases down to a
// settled glow over this window, then the rAF pulse loop stops. Keep it short
// and finite — never a permanent animation loop.
const PULSE_DURATION_MS = 800;
// Settled glow radius (logical px) that persists while a preliminary marker
// exists, and the extra radius added at the pulse peak (fresh fire).
const PRELIM_GLOW_BASE = 8;
const PRELIM_GLOW_PEAK_EXTRA = 8;
// Near-solid opacity for preliminary markers (was ~0.4 faint). Confirmed = 1.0.
const PRELIM_ALPHA = 0.9;

// ============================================================================
// Types
// ============================================================================

export interface BosOverlayItem {
  swingTime: number; // seconds — the swing pivot time (left clamp for the segment)
  breakTime: number; // seconds — the break candle time (right end of the segment)
  price: number; // break level (the y of the line)
  text: string; // 'BOS' | 'CHoCH'
  color: string;
  lineStyle: number; // 0 = solid, 2 = dashed
  lineWidth: number; // logical pixels
  lineLengthBars: number; // segment length in bars, measured back from breakTime
  showLabel: boolean; // whether to draw the centered text label on the line
  baseGrade?: string; // predictive grade shown as the primary letter (lookahead-free)
  grade?: string; // resolved grade (fallback when baseGrade absent, e.g. old markers)
  retestStatus?: string; // 'pending'|'held'|'failed'|'none' — drives the confirmation badge
  pending?: boolean; // preliminary/unconfirmed instant fire — drawn dimmed + dashed
}

/** Tier color for a grade letter (A+ brightest green ... B orange, F red). */
function gradeColor(grade: string): string {
  switch (grade) {
    case 'A+': return '#00e676';
    case 'A': return '#66bb6a';
    case 'B+': return '#ffb300';
    case 'B': return '#ff7043';
    case 'F': return '#ef5350';
    default: return '#b0bec5';
  }
}

// Backwards-compatible alias: ChartView previously imported `BosLabel`.
export type BosLabel = BosOverlayItem;

interface BosRendererItem {
  startX: number; // logical px — left end of the segment
  breakX: number; // logical px — right end of the segment
  levelY: number; // logical px — the level's y coordinate
  text: string;
  color: string;
  lineStyle: number;
  lineWidth: number;
  showLabel: boolean;
  grade: string; // predictive (base) grade letter; '' when grading disabled
  gradeColor: string; // tier color of the predictive grade
  badge: string; // retest confirmation: '✓' held, '✗' failed, '' otherwise
  badgeColor: string;
  pending: boolean; // retest unresolved — dim the grade
  preliminary: boolean; // unconfirmed instant fire — near-solid + glow/pulse
  pulse: number; // 0..1 pulse intensity for preliminary glow (0 = settled)
  /** Shared layout folded this label into a "+N" chip: draw the segment only. */
  labelHidden?: boolean;
}

/** Label font and padding (CSS px), as the renderer draws it. */
const LABEL_FONT_PX = 10;
const LABEL_PAD = 3;

/** The label's full text pieces. */
function labelText(item: Pick<BosRendererItem, 'text' | 'grade' | 'badge'>): string {
  return `${item.text}${item.grade ? ` ${item.grade}` : ''}${item.badge ? ` ${item.badge}` : ''}`;
}

// ============================================================================
// Renderer
// ============================================================================

class BosOverlayRenderer implements IPrimitivePaneRenderer {
  private _items: BosRendererItem[] = [];

  update(items: BosRendererItem[]): void {
    this._items = items;
  }

  draw(target: any): void {
    target.useBitmapCoordinateSpace((scope: any) => {
      const ctx = scope.context as CanvasRenderingContext2D;
      const horizontalPixelRatio = scope.horizontalPixelRatio as number;
      const verticalPixelRatio = scope.verticalPixelRatio as number;

      const fontSize = Math.round(10 * horizontalPixelRatio);
      const padding = 3 * horizontalPixelRatio;

      for (const item of this._items) {
        const startPx = item.startX * horizontalPixelRatio;
        const breakPx = item.breakX * horizontalPixelRatio;
        const levelPy = item.levelY * verticalPixelRatio;

        // Preliminary (unconfirmed) breaks render near-solid with a colored glow
        // so they read as "hot/forming" the moment they fire; a short pulse eases
        // the glow from a bright peak down to a settled radius. On confirmation
        // (preliminary=false) they revert to plain solid/full, no glow.
        const baseAlpha = item.preliminary ? PRELIM_ALPHA : 1.0;
        // Settled glow (base) + pulse peak boost, scaled to bitmap space.
        const glowBlur = item.preliminary
          ? (PRELIM_GLOW_BASE + item.pulse * PRELIM_GLOW_PEAK_EXTRA) * horizontalPixelRatio
          : 0;

        // --- Line segment -------------------------------------------------
        ctx.save();
        ctx.globalAlpha = baseAlpha;
        ctx.strokeStyle = item.color;
        // Scale logical line width to bitmap space (use vertical ratio: the
        // stroke is horizontal, so its thickness is a vertical dimension).
        ctx.lineWidth = Math.max(1, item.lineWidth * verticalPixelRatio);
        if (item.preliminary) {
          ctx.shadowColor = item.color;
          ctx.shadowBlur = glowBlur;
        }
        // Preliminary no longer forces a dashed line — the glow carries the
        // "provisional" signal instead. Honour an explicit dashed style only.
        const dashed = item.lineStyle === 2;
        ctx.setLineDash(dashed ? [4 * horizontalPixelRatio, 4 * horizontalPixelRatio] : []);
        ctx.beginPath();
        ctx.moveTo(startPx, levelPy);
        ctx.lineTo(breakPx, levelPy);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.shadowBlur = 0; // reset so the glow never bleeds onto later draws
        ctx.restore();

        // --- Centered label (line runs through it) ------------------------
        if (item.showLabel && !item.labelHidden) {
          const midPx = (startPx + breakPx) / 2;

          ctx.save();
          ctx.globalAlpha = baseAlpha;
          ctx.font = `800 ${fontSize}px sans-serif`;
          ctx.textBaseline = 'middle';

          // Label = structure text (BOS/CHoCH) + predictive grade (tier color) +
          // retest badge (✓ held / ✗ failed). The letter is the lookahead-free
          // grade; the badge is the after-the-fact confirmation. Pending dims the
          // grade since the retest hasn't resolved.
          const base = item.text;
          const gradeStr = item.grade ? ` ${item.grade}` : '';
          const badgeStr = item.badge ? ` ${item.badge}` : '';
          ctx.textAlign = 'left';
          const baseWidth = ctx.measureText(base).width;
          const gradeWidth = gradeStr ? ctx.measureText(gradeStr).width : 0;
          const badgeWidth = badgeStr ? ctx.measureText(badgeStr).width : 0;
          const totalWidth = baseWidth + gradeWidth + badgeWidth;
          const leftPx = midPx - totalWidth / 2;

          // Preliminary label glows in its own hue so the forming break grabs
          // the eye. Applied to the pill + structure text; cleared before the
          // grade/badge so their colors stay crisp and the glow doesn't stack.
          if (item.preliminary) {
            ctx.shadowColor = item.color;
            ctx.shadowBlur = glowBlur;
          }

          // Background pill spanning the whole label.
          ctx.fillStyle = 'rgba(0, 0, 0, 0.65)';
          ctx.fillRect(
            leftPx - padding,
            levelPy - fontSize / 2 - padding,
            totalWidth + padding * 2,
            fontSize + padding * 2
          );

          // Structure text.
          ctx.fillStyle = item.color;
          ctx.fillText(base, leftPx, levelPy);

          ctx.shadowBlur = 0; // stop glow before grade/badge (and later items)

          // Predictive grade, tier-colored. Dim while the retest is still pending.
          if (gradeStr) {
            ctx.globalAlpha = baseAlpha * (item.pending ? 0.65 : 1.0);
            ctx.fillStyle = item.gradeColor;
            ctx.fillText(gradeStr, leftPx + baseWidth, levelPy);
            ctx.globalAlpha = baseAlpha;
          }

          // Retest confirmation badge.
          if (badgeStr) {
            ctx.fillStyle = item.badgeColor;
            ctx.fillText(badgeStr, leftPx + baseWidth + gradeWidth, levelPy);
          }
          ctx.restore();
        }
      }
    });
  }
}

// ============================================================================
// Pane View
// ============================================================================

class BosOverlayPaneView implements IPrimitivePaneView {
  private _source: BosLabelPrimitive;
  private _renderer: BosOverlayRenderer;

  constructor(source: BosLabelPrimitive) {
    this._source = source;
    this._renderer = new BosOverlayRenderer();
  }

  zOrder(): PrimitivePaneViewZOrder {
    return 'top'; // Draw on top so the segment/label sit above the candles.
  }

  renderer(): IPrimitivePaneRenderer {
    this._renderer.update(this._source.getRendererData());
    return this._renderer;
  }
}

// ============================================================================
// Primitive
// ============================================================================

export class BosLabelPrimitive implements ISeriesPrimitive<Time> {
  private _items: BosOverlayItem[] = [];
  private _paneView: BosOverlayPaneView;
  private _requestUpdate?: () => void;
  private _series?: SeriesAttachedParameter<Time>['series'];
  private _chart?: SeriesAttachedParameter<Time>['chart'];

  // --- Preliminary glow pulse state ---
  private _knownPrelimKeys = new Set<string>(); // preliminary markers seen last update
  private _pulseStart = 0; // performance.now() when the current pulse began
  private _pulseActive = false; // whether a finite pulse rAF loop is running
  private _rafId?: number; // handle to cancel the pulse loop on detach
  /**
   * Shared marker layout: each label is a FIXED box on its own line (the line
   * runs through it), so other markers flow around it; a label that would sit
   * on an earlier one folds into a "+N" chip and its segment still draws.
   */
  private _layout = new MarkerLayoutClient('bos', (ctx) => this._layoutItems(ctx));

  constructor() {
    this._paneView = new BosOverlayPaneView(this);
  }

  attached(param: SeriesAttachedParameter<Time>): void {
    this._series = param.series;
    this._chart = param.chart;
    this._requestUpdate = param.requestUpdate;
    this._layout.setAttached(true);
  }

  /** Join the chart's shared marker layout (null: every label draws). */
  setMarkerLayout(reg: MarkerLayoutRegistry | null): void {
    this._layout.setRegistry(reg);
  }

  private _layoutItems(ctx: LayoutContext): LayoutItem[] {
    const out: LayoutItem[] = [];
    for (const r of this._rendererItems()) {
      if (!r.showLabel) continue;
      const text = labelText(r);
      const w = ctx.measure(text, `800 ${LABEL_FONT_PX}px sans-serif`) + LABEL_PAD * 2;
      out.push({
        key: `${r.key}`,
        time: r.breakTime,
        x: (r.startX + r.breakX) / 2,
        side: 'above',
        anchorY: r.levelY,
        w: Math.ceil(w),
        h: LABEL_FONT_PX + LABEL_PAD * 2,
        priority: LAYOUT_PRIORITY.fixed,
        fixed: true,
        label: text,
      });
    }
    return out;
  }

  detached(): void {
    this._layout.setAttached(false);
    this._series = undefined;
    this._chart = undefined;
    this._requestUpdate = undefined;
    if (this._rafId !== undefined) {
      cancelAnimationFrame(this._rafId);
      this._rafId = undefined;
    }
    this._pulseActive = false;
    this._knownPrelimKeys.clear();
  }

  paneViews(): IPrimitivePaneView[] {
    return [this._paneView];
  }

  updateAllViews(): void {
    // Called when chart needs to update views
  }

  /**
   * Replace the full set of BOS overlay items (line + label) and request a
   * redraw. Pass [] to clear.
   */
  update(items: BosOverlayItem[]): void {
    // Detect newly-appeared preliminary markers so a fresh fire triggers the
    // short glow pulse (existing preliminary markers do not re-pulse).
    const prelimKeys = new Set<string>();
    for (const it of items) {
      if (it.pending === true) {
        prelimKeys.add(`${it.breakTime}|${it.price}|${it.text}`);
      }
    }
    let hasNewPrelim = false;
    for (const k of prelimKeys) {
      if (!this._knownPrelimKeys.has(k)) {
        hasNewPrelim = true;
        break;
      }
    }
    this._knownPrelimKeys = prelimKeys;

    this._items = items;
    this._layout.invalidate();
    if (hasNewPrelim) {
      this._startPulse();
    }
    this._requestUpdate?.();
  }

  /**
   * Kick a short, finite pulse: re-request redraws via requestAnimationFrame for
   * PULSE_DURATION_MS so the preliminary glow eases from its bright peak down to
   * the settled radius, then stops. If a pulse is already running, just reset its
   * start time so the newest fire re-flashes without stacking rAF loops. This is
   * intentionally NOT a permanent animation loop.
   */
  private _startPulse(): void {
    this._pulseStart = performance.now();
    if (this._pulseActive) return; // existing loop will pick up the new start time
    this._pulseActive = true;
    const tick = () => {
      const elapsed = performance.now() - this._pulseStart;
      this._requestUpdate?.();
      if (elapsed < PULSE_DURATION_MS) {
        this._rafId = requestAnimationFrame(tick);
      } else {
        this._pulseActive = false;
        this._rafId = undefined;
        this._requestUpdate?.(); // final settled frame
      }
    };
    this._rafId = requestAnimationFrame(tick);
  }

  /** Current pulse intensity, 1 (fresh fire) easing to 0 (settled). */
  private _pulseIntensity(): number {
    if (!this._pulseActive) return 0;
    const elapsed = performance.now() - this._pulseStart;
    const t = Math.min(1, Math.max(0, elapsed / PULSE_DURATION_MS));
    const fade = 1 - t;
    return fade * fade; // ease-out
  }

  /**
   * Convert each item's (swingTime, breakTime, price) into screen coordinates.
   * The segment runs from `startX` to `breakX` at `levelY`, where:
   *   startX = breakX - lineLengthBars * barSpacing, clamped to swingX so the
   *   segment never extends left past the swing pivot.
   * Items whose break coordinate or level coordinate is null (off-screen / no
   * data) are skipped.
   */
  getRendererData(): BosRendererItem[] {
    const data = this._rendererItems();
    const shared = this._layout.placed();
    if (shared) {
      const kept = new Set(shared.map((p) => p.item.key));
      for (const d of data) if (d.showLabel && !kept.has(d.key)) d.labelHidden = true;
    }
    return data;
  }

  private _rendererItems(): (BosRendererItem & { key: string; breakTime: number })[] {
    if (!this._series || !this._chart || this._items.length === 0) {
      return [];
    }

    const timeScale = this._chart.timeScale();
    const barSpacing = timeScale.options().barSpacing; // pixels per bar
    const data: (BosRendererItem & { key: string; breakTime: number })[] = [];

    // Shared pulse intensity for all preliminary markers this frame.
    const pulse = this._pulseIntensity();

    for (const item of this._items) {
      const levelY = this._series.priceToCoordinate(item.price);
      if (levelY === null) continue;
      const breakXc = timeScale.timeToCoordinate(item.breakTime as Time);
      if (breakXc === null) continue;

      // Span the full structure: from the swing pivot candle to the break candle,
      // extended half a bar on each side so the segment reaches the OUTER edges of
      // the start and end candles (timeToCoordinate gives the candle center, so
      // without the +/- half-bar the line would stop at each candle's midpoint and
      // not connect them completely).
      const half = barSpacing / 2;
      const swingX = timeScale.timeToCoordinate(item.swingTime as Time);
      // Fall back to the lineLengthBars cap only if the swing is off the loaded
      // range (no coordinate); otherwise always connect swing -> break.
      const startX = (swingX !== null ? swingX : breakXc - item.lineLengthBars * barSpacing) - half;
      const breakX = breakXc + half;

      // Primary letter is the PREDICTIVE (base) grade — what was knowable at the
      // break. The retest outcome shows as a separate confirmation badge, not by
      // mutating the letter, so the lookahead-free grade stays visible.
      const grade = item.baseGrade ?? item.grade ?? '';
      const rs = item.retestStatus;
      const badge = rs === 'held' ? '✓' : rs === 'failed' ? '✗' : '';
      const badgeColor = rs === 'held' ? '#00e676' : rs === 'failed' ? '#ef5350' : '#b0bec5';
      data.push({
        startX,
        breakX,
        levelY,
        text: item.text,
        color: item.color,
        lineStyle: item.lineStyle,
        lineWidth: item.lineWidth,
        showLabel: item.showLabel,
        grade,
        gradeColor: gradeColor(grade),
        badge,
        badgeColor,
        pending: rs === 'pending',
        preliminary: item.pending === true,
        pulse,
        key: `${item.breakTime}|${item.price}|${item.text}`,
        breakTime: item.breakTime,
      });
    }

    return data;
  }
}

// ============================================================================
// Factory Function
// ============================================================================

export function createBosLabelPrimitive(): BosLabelPrimitive {
  return new BosLabelPrimitive();
}
